import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toDate } from '@/lib/date-display.js';

// URL-backed filter state for the Pipeline kanban (spec §3.6).
// Bookmarkable filtered views.
//
// 2026-05-20 (Vision §V2 fix #5): extended with carrier multi-select,
// referral-partner multi-select, aging buckets, and custom date range
// (from/to) as alternatives to the canned 30/60/100 windows. All
// values persist in the URL query string so a broker can bookmark a
// scoped view (e.g. "Beacon quotes I've slept on for 7+ days").

const DEFAULTS = {
  scope: 'all',
  range: 'any',
  sort: 'urgency',
  q: '',
  closed: '0',
  carriers: '',    // comma-separated carrier name list
  partners: '',    // comma-separated referral_partner_id list
  aircraft: '',       // comma-separated make/model list (FAA make_model)
  aircraft_type: '',  // comma-separated ICAO type codes (icao_type rollup)
  aging: 'any',    // any / 0-3 / 4-7 / 8-14 / 15-30 / 30+
  from: '',        // ISO yyyy-mm-dd — custom date range start
  to: '',          // ISO yyyy-mm-dd — custom date range end
};

// All / Mine / Unassigned (deals-door-1 T19; "team" filtered nothing and is gone: an old ?scope=team reads as All).
export const SCOPES = ['all', 'mine', 'unassigned'];

function decodeList(value) {
  if (!value) return [];
  return String(value).split(',').map((s) => s.trim()).filter(Boolean);
}

function encodeList(list) {
  if (!Array.isArray(list) || list.length === 0) return '';
  return list.map((s) => String(s).trim()).filter(Boolean).join(',');
}

export default function usePipelineFilters() {
  const [params, setParams] = useSearchParams();

  const filters = useMemo(
    () => ({
      scope: SCOPES.includes(params.get('scope')) ? params.get('scope') : DEFAULTS.scope,
      range: params.get('range') || DEFAULTS.range,
      sort: params.get('sort') || DEFAULTS.sort,
      q: params.get('q') || DEFAULTS.q,
      closed: params.get('closed') === '1',
      carriers: decodeList(params.get('carriers')),
      partners: decodeList(params.get('partners')),
      aircraft: decodeList(params.get('aircraft')),
      aircraft_type: decodeList(params.get('aircraft_type')),
      aging: params.get('aging') || DEFAULTS.aging,
      from: params.get('from') || DEFAULTS.from,
      to: params.get('to') || DEFAULTS.to,
    }),
    [params]
  );

  function update(patch) {
    const next = new URLSearchParams(params);
    for (const [k, raw] of Object.entries(patch)) {
      // List-valued filters serialize to CSV; everything else uses the
      // shared "default-equals = remove from URL" rule that keeps URLs
      // clean for default state.
      let v = raw;
      if (k === 'carriers' || k === 'partners' || k === 'aircraft' || k === 'aircraft_type') {
        v = Array.isArray(v) ? encodeList(v) : '';
      }
      if (v === DEFAULTS[k] || v === false || v === '' || v == null) {
        next.delete(k);
      } else {
        next.set(k, v === true ? '1' : String(v));
      }
    }
    setParams(next, { replace: true });
  }

  function reset() {
    setParams(new URLSearchParams(), { replace: true });
  }

  return { filters, update, reset };
}

// Aging buckets — picks the most recent of policy_expiration vs
// stage_changed_at vs last_market_activity_at to compute "days since
// the deal moved." Items with no signal default to bucket 'any'.
const AGING_RANGES = {
  '0-3': [0, 3],
  '4-7': [4, 7],
  '8-14': [8, 14],
  '15-30': [15, 30],
  '30+': [31, Infinity],
};

function daysSinceLastActivity(i) {
  // Prefer the explicit field if buildPipelineItem populated it;
  // otherwise fall back to computing from the most recent timestamp
  // available on the item.
  if (i.days_since_last_activity != null) return i.days_since_last_activity;
  const candidates = [
    i.last_market_activity_at,
    i.stage_changed_at,
    i.invite_sent,
    i.session_created,
  ]
    .filter(Boolean)
    .map((s) => toDate(s)?.getTime())
    .filter(Number.isFinite);
  if (candidates.length === 0) return null;
  return Math.floor((Date.now() - Math.max(...candidates)) / 86400000);
}

// Apply filters to a raw pipeline items array. Pure — no React state.
// Scope (All / Mine / Unassigned) is NOT applied here: the server answers it from the policy's producer (deals-door-1
// T19, GET /api/tracon/pipeline?scope=), so the items handed in are already the scope's.
export function applyFilters(items, filters) {
  if (!Array.isArray(items)) return items;
  let out = items;
  // Custom date range wins over canned range buckets when set —
  // brokers can pin a quarter or a specific renewal window.
  if (filters.from || filters.to) {
    const fromMs = filters.from ? toDate(filters.from)?.getTime() ?? null : null;
    const toMs = filters.to
      ? (toDate(filters.to)?.getTime() ?? 0) + 86400000 - 1 // inclusive
      : null;
    out = out.filter((i) => {
      if (!i.policy_expiration) return true; // no data — let it through
      const ms = toDate(i.policy_expiration)?.getTime() ?? NaN;
      if (!Number.isFinite(ms)) return true;
      if (fromMs != null && ms < fromMs) return false;
      if (toMs != null && ms > toMs) return false;
      return true;
    });
  } else if (filters.range && filters.range !== 'any') {
    const maxDays = parseInt(filters.range, 10);
    if (Number.isFinite(maxDays)) {
      out = out.filter((i) => {
        const d = i.days_until_expiration;
        if (d == null) return true;
        return d <= maxDays;
      });
    }
  }
  // Carrier multi-select — match by canonical Vendor.id (Vision §V2 fix
  // #1, 2026-05-20). Pipeline items now carry `carrier_id` (deal-level
  // Vendor lookup id) + `quotes.carrier_ids[]` (every quote's carrier).
  // Filter values are Vendor ids sourced from /api/tracon/carriers, so
  // 'CHUBB / ACE American Insurance Company' and 'Chubb/ACE American'
  // dedupe to one record and don't both show up in the dropdown.
  if (filters.carriers && filters.carriers.length > 0) {
    const allowed = new Set(filters.carriers.map(String));
    out = out.filter((i) => {
      if (i.carrier_id && allowed.has(String(i.carrier_id))) return true;
      const quoteIds = i.quotes?.carrier_ids || [];
      return quoteIds.some((id) => allowed.has(String(id)));
    });
  }
  // Referral-partner multi-select. Match against the deal's
  // referral.referral_partner_id (snapshot on the pipeline item from
  // buildPipelineItem).
  if (filters.partners && filters.partners.length > 0) {
    const allowed = new Set(filters.partners.map(String));
    out = out.filter((i) => {
      const id = String(i.referral?.referral_partner_id || '');
      return id && allowed.has(id);
    });
  }
  // Aircraft facet — make/model AND ICAO type/category, UNION within the facet
  // (selecting type 'C172' or make/model 'Cirrus SR22' shows deals matching
  // either). A fleet deal matches if ANY tail matches. Case-insensitive.
  const mmSel = new Set((filters.aircraft || []).map((s) => String(s).trim().toUpperCase()));
  const tySel = new Set((filters.aircraft_type || []).map((s) => String(s).trim().toUpperCase()));
  if (mmSel.size || tySel.size) {
    out = out.filter((i) =>
      (i.aircraft || []).some((a) =>
        (mmSel.size && a.make_model && mmSel.has(String(a.make_model).trim().toUpperCase())) ||
        (tySel.size && a.icao_type && tySel.has(String(a.icao_type).trim().toUpperCase()))
      )
    );
  }
  // Aging filter — bucket items by days_since_last_activity.
  if (filters.aging && filters.aging !== 'any') {
    const range = AGING_RANGES[filters.aging];
    if (range) {
      const [min, max] = range;
      out = out.filter((i) => {
        const d = daysSinceLastActivity(i);
        if (d == null) return false; // exclude unknown when aging is specifically requested
        return d >= min && d <= max;
      });
    }
  }
  if (filters.q && filters.q.trim()) {
    const needle = filters.q.trim().toLowerCase();
    out = out.filter((i) => {
      const haystack = [
        i.named_insured,
        i.deal_name,
        i.app_number,
        i.primary_contact?.name,
        ...(i.aircraft || []).map((a) => a.tail_number),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }
  return out;
}

// Distinct partners aggregated from pipeline items. Carriers used to
// come through here too, but as of Vision §V2 fix #1 (2026-05-20) the
// canonical carrier list is served by /api/tracon/carriers (Vendors
// module) and surfaced by BoardFilters directly — no longer derived
// from the visible pipeline. distinctCarriers is retained as a no-op
// alias for any test still grepping for it.
export function distinctCarriers() {
  return [];
}

// Distinct aircraft make/models aggregated from the visible board (FAA
// make_model on each pipeline item's aircraft[]). The facet directory is
// whatever's on the broker's board — same approach as partners. id = the
// canonical (trimmed) make_model string the filter persists; label = display.
export function distinctAircraft(items) {
  if (!Array.isArray(items)) return [];
  const byKey = new Map(); // UPPER key → display label (first-seen casing)
  for (const i of items) {
    for (const a of (i.aircraft || [])) {
      const mm = (a.make_model || '').trim();
      if (!mm) continue;
      const key = mm.toUpperCase();
      if (!byKey.has(key)) byKey.set(key, mm);
    }
  }
  return Array.from(byKey.values())
    .map((mm) => ({ id: mm, label: mm }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// Distinct ICAO type/category buckets from the visible board. Each pipeline
// aircraft carries icao_type (Doc 8643 code, e.g. C172 — rolls up variants)
// + icao_display, stamped server-side via utils/icao-family.js. Aircraft with
// no ICAO match (icao_type null) are omitted — they're filterable by make/model
// instead. id = icao_type code the filter persists; label = display name.
export function distinctAircraftTypes(items) {
  if (!Array.isArray(items)) return [];
  const byCode = new Map(); // CODE → display label
  for (const i of items) {
    for (const a of (i.aircraft || [])) {
      const code = (a.icao_type || '').trim();
      if (!code) continue;
      if (!byCode.has(code)) byCode.set(code, a.icao_display || code);
    }
  }
  return Array.from(byCode.entries())
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function distinctPartners(items) {
  if (!Array.isArray(items)) return [];
  // {id → label} so the UI can show partner_ref_id prefix as a hint
  // when available without forcing a partner-directory fetch.
  const byId = new Map();
  for (const i of items) {
    const id = i.referral?.referral_partner_id;
    if (!id) continue;
    if (!byId.has(id)) {
      // Use partner_ref_id prefix as a coarse label (e.g. CAN-1234 →
      // "CAN"). The broker recognizes the prefix; a full directory
      // lookup is V1.5+.
      const refId = i.referral?.partner_ref_id || '';
      const prefix = refId.split('-')[0] || '(partner)';
      byId.set(id, prefix);
    }
  }
  return Array.from(byId.entries())
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
